import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { tap, catchError, of } from 'rxjs';

import { AppState } from 'src/app/reducers';
import { LoggerService } from '@services/logger.service';
import { PostService } from 'src/app/core/services/post.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface StoryRunParamItem {
  label: string;
  value: any;
}

@Component({
  selector: 'app-story-run-params',
  templateUrl: './story-run-params.component.html',
  styleUrls: ['./story-run-params.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StoryRunParamsComponent implements OnChanges {
  @Input() postId: string;

  storyParams: any = null;
  loading = false;
  notFound = false;
  paramItems: StoryRunParamItem[] = [];
  promptParamItems: StoryRunParamItem[] = [];

  private destroyRef = inject(DestroyRef);
  private postService = inject(PostService);
  private logger = inject(LoggerService);

  constructor(
    protected store: Store<AppState>,
    private translateService: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postId'] && this.postId) {
      this.loadStoryParams();
    }
  }

  private loadStoryParams(): void {
    this.loading = true;
    this.notFound = false;
    this.storyParams = null;
    this.paramItems = [];
    this.promptParamItems = [];

    this.postService
      .getStoryParams(this.postId)
      .pipe(
        tap((storyParams) => {
          this.loading = false;
          if (!storyParams) {
            this.notFound = true;
            return;
          }
          this.storyParams = storyParams;
          this.buildParamItems(storyParams);
        }),
        catchError((err) => {
          this.logger.error('Failed to load story params', err);
          this.loading = false;
          this.notFound = true;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private buildParamItems(params: any): void {
    if (!params) {
      return;
    }

    const mainKeys: Array<{ key: string; labelKey: string }> = [
      {
        key: 'prompt_template_id',
        labelKey: 'story-run-params.label.prompt-template-id',
      },
      { key: 'clusters', labelKey: 'story-run-params.label.clusters' },
      {
        key: 'distance_threshold',
        labelKey: 'story-run-params.label.distance-threshold',
      },
      {
        key: 'sanitize_mentions',
        labelKey: 'story-run-params.label.sanitize-mentions',
      },
      {
        key: 'sanitize_urls',
        labelKey: 'story-run-params.label.sanitize-urls',
      },
      { key: 'sort', labelKey: 'story-run-params.label.sort' },
      {
        key: 'sample_metric',
        labelKey: 'story-run-params.label.sample-metric',
      },
      {
        key: 'sampling_percentile_groups',
        labelKey: 'story-run-params.label.sampling-percentile-groups',
      },
      {
        key: 'sampling_percentage_per_group',
        labelKey: 'story-run-params.label.sampling-percentage-per-group',
      },
      {
        key: 'sample_length',
        labelKey: 'story-run-params.label.sample-length',
      },
    ];

    this.paramItems = mainKeys
      .filter(({ key }) => params[key] !== undefined && params[key] !== null)
      .map(({ key, labelKey }) => ({
        label: this.translateService.instant(labelKey),
        value: params[key],
      }));

    const promptParams = params.prompt_params;
    if (promptParams && typeof promptParams === 'object') {
      this.promptParamItems = Object.keys(promptParams).map((key) => ({
        label: key,
        value: promptParams[key],
      }));
    }
  }

  formatValue(value: any): string {
    if (value === true) return 'true';
    if (value === false) return 'false';
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
}
